import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Trophy, 
  TrendingUp, 
  Users, 
  BarChart3, 
  Target,
  Activity
} from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Skeleton from '../components/common/Skeleton';
import { V1Team, V1StandingEntry } from '../types';
import { v1Api } from '../services/api';
import { RosterPageV2 } from '../components/roster';

interface TeamWeeklyPerformance {
  team_id: number;
  team_name: string;
  week_range: { start: number; end: number };
  weekly_performance: Array<{
    week: number;
    total_points: number;
    starter_points: number;
    bench_points: number;
    projected_points: number;
    projection_diff: number;
    starters_count: number;
    bench_count: number;
    matchup?: {
      opponent_id: number;
      team_score: number;
      opponent_score: number;
      result: 'win' | 'loss' | 'tie';
      margin: number;
    };
  }>;
  summary: {
    total_weeks: number;
    avg_points: number;
    total_points: number;
    best_week: { week: number; points: number };
    worst_week: { week: number; points: number };
  };
}


const TeamPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<V1Team | null>(null);
  const [weeklyPerformance, setWeeklyPerformance] = useState<TeamWeeklyPerformance | null>(null);
  const [teamStanding, setTeamStanding] = useState<V1StandingEntry | null>(null);
  const [espnLeagueId, setEspnLeagueId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'roster' | 'performance'>('roster');

  const fetchTeamData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const teamIdNum = parseInt(teamId!);
      
      // First get team data to get the league ID
      const teamData = await v1Api.teams.getById(teamIdNum);
      
      // Get full league data to access ESPN league ID
      const leagueData = await v1Api.leagues.getById(teamData.league.id);
      
      const [, performanceData, standingsData] = await Promise.all([
        v1Api.teams.getRoster(teamIdNum),
        v1Api.teams.getWeeklyPerformance(teamIdNum),
        v1Api.analytics.getStandings(teamData.league.id)
      ]);
      
      setTeam(teamData);
      setWeeklyPerformance(performanceData);
      setEspnLeagueId(leagueData.espn_league_id || null);
      
      // Find this team's standing
      const teamStandingEntry = standingsData?.standings?.find((s: V1StandingEntry) => s.team_id === teamIdNum);
      setTeamStanding(teamStandingEntry || null);
    } catch (err) {
      setError('Failed to load team data');
      console.error('Team fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (teamId) {
      fetchTeamData();
    }
  }, [teamId, fetchTeamData]);



  if (!teamId) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-100">Invalid team ID</h2>
          <Link to="/">
            <Button className="mt-4">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-12 w-80 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="p-6 text-center">
          <div className="text-accent-danger mb-4">{error || 'Failed to load team'}</div>
          <Button onClick={fetchTeamData} className="mr-4">Retry</Button>
          <Link to="/">
            <Button variant="secondary">Back to Home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => {
              if (espnLeagueId) {
                navigate(`/leagues/espn/${espnLeagueId}/overview`);
              } else {
                navigate(-1); // Fallback to browser back if ESPN ID not available
              }
            }}
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to League Overview
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-100 mb-2">{team.name}</h1>
              <div className="flex items-center space-x-6 text-slate-400">
                {team.owner_names && team.owner_names.length > 0 && (
                  <span className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    <span className="text-lg font-medium text-slate-200">
                      {team.owner_names.join(', ')}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Final Playoff Standing */}
          {teamStanding && (
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Final Playoff Standing</p>
                  <p className="text-2xl font-bold text-slate-100">
                    #{teamStanding.rank}
                  </p>
                  <p className="text-sm text-slate-400">
                    {teamStanding.total_season.wins}-{teamStanding.total_season.losses} ({(teamStanding.total_season.win_percentage * 100).toFixed(1)}%)
                  </p>
                </div>
                <Trophy className="w-8 h-8 text-yellow-400" />
              </div>
            </Card>
          )}

          {/* Regular Season Standing */}
          {teamStanding && (
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Regular Season Standing</p>
                  <p className="text-2xl font-bold text-slate-100">
                    #{teamStanding.regular_season.rank}
                  </p>
                  <p className="text-sm text-slate-400">
                    {teamStanding.regular_season.wins}-{teamStanding.regular_season.losses} ({(teamStanding.regular_season.win_percentage * 100).toFixed(1)}%)
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-green-400" />
              </div>
            </Card>
          )}

          {/* Regular Season Points For */}
          {teamStanding && (
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Regular Season Points For</p>
                  <p className="text-2xl font-bold text-slate-100">
                    {Number(teamStanding.regular_season.points_for).toFixed(1)}
                  </p>
                  <p className="text-sm text-slate-400">
                    {Number(teamStanding.regular_season.avg_points_for || 0).toFixed(1)} per game
                  </p>
                </div>
                <Target className="w-8 h-8 text-green-400" />
              </div>
            </Card>
          )}

          {/* Regular Season Points Against */}
          {teamStanding && (
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Regular Season Points Against</p>
                  <p className="text-2xl font-bold text-slate-100">
                    {Number(teamStanding.regular_season.points_against).toFixed(1)}
                  </p>
                  <p className="text-sm text-slate-400">
                    {Number(teamStanding.regular_season.avg_points_against || 0).toFixed(1)} per game
                  </p>
                </div>
                <Activity className="w-8 h-8 text-red-400" />
              </div>
            </Card>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-slate-800 p-1 rounded-lg w-fit">
            {[
              { key: 'roster', label: 'Roster', icon: Users },
              { key: 'performance', label: 'Performance', icon: TrendingUp }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as any)}
                className={`px-4 py-2 rounded-md flex items-center space-x-2 transition-colors ${
                  activeTab === key
                    ? 'bg-accent-primary/15 text-accent-primary border-b-2 border-accent-primary'
                    : 'text-text-secondary hover:text-text-primary hover:bg-background-hover'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'roster' && (
          <RosterPageV2
            teamId={parseInt(teamId!)}
            initialWeek={14}
            onViewDraftBoard={() => {
              // TODO: Implement draft board navigation
              console.log('View draft board clicked');
            }}
          />
        )}

        {activeTab === 'performance' && weeklyPerformance && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-xl font-bold text-slate-100 mb-4">Weekly Performance</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-100">
                    {Number(
                      weeklyPerformance.weekly_performance
                        .filter(week => week.starter_points > 0)
                        .reduce((sum, week) => sum + week.starter_points, 0) /
                      weeklyPerformance.weekly_performance.filter(week => week.starter_points > 0).length
                    ).toFixed(1)}
                  </div>
                  <div className="text-slate-400">Avg Starter Points</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {Number(weeklyPerformance.summary.best_week.points).toFixed(1)}
                  </div>
                  <div className="text-slate-400">Best Week ({weeklyPerformance.summary.best_week.week})</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-400">
                    {Number(weeklyPerformance.summary.worst_week.points).toFixed(1)}
                  </div>
                  <div className="text-slate-400">Worst Week ({weeklyPerformance.summary.worst_week.week})</div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Week</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Score</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Opponent Score</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Projected Score</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyPerformance.weekly_performance
                      .filter(week => week.total_points > 0 || week.matchup)
                      .map((week) => (
                      <tr key={week.week} className="border-b border-slate-800 hover:bg-slate-800">
                        <td className="py-3 px-4">
                          <span className="text-slate-100 font-medium">Week {week.week}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-slate-100 font-bold">
                            {Number(week.starter_points).toFixed(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-slate-100">
                            {week.matchup ? Number(week.matchup.opponent_score).toFixed(1) : '—'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-slate-400">
                            {Number(week.projected_points).toFixed(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {week.matchup ? (
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                              week.matchup.result === 'win' 
                                ? 'bg-green-900/20 text-green-400' 
                                : 'bg-red-900/20 text-red-400'
                            }`}>
                              {week.matchup.result.toUpperCase()}
                            </span>
                          ) : (
                            <span className="text-slate-500 text-xs">BYE</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

      </div>
    </div>
  );
};

export default TeamPage;